import HiddenInput from "@/components/form/components/HiddenInput";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Alert, App as AntApp, Checkbox, Col, Divider, Form, Modal, Row, Tree } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAdminUpdateAgentUserAccount,
  useEnumeration,
  useGetAgentEnumerations,
  useGetPermissionDefinitions,
} from "../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import { AntTreeStrictCheckedProps } from "../../../../common/types";
import { UUID } from "../../../../typings/global";
import { AgentType } from "../../../agent/enums";
import { formatAgentName } from "../../../agent/utils";
import { useIsSystemAdmin } from "../../../auth/hooks";
import { userChanged } from "../../../auth/session";
import { AgentSelect } from "../../../enumerations/components/form/AgentSelect";
import { EnumerationType } from "../../../enumerations/enums";
import { AdminCreateUpdateAgentUserAccount, AgentUserAccount, UserAdminView } from "../../types";
import {
  buildPermissionTreeNodes,
  filterPermissionsWithPrerequisites,
  hasConfirmedAgentUserAccount,
} from "../../utils";
import { DefaultPermissionsFormPart } from "./DefaultPermissionsFormPart";

interface Props {
  open: boolean;
  user?: UserAdminView;
  account?: AgentUserAccount;
  onFormCancel: () => void;
}

const colSpan = 8;

const contractImportPredisposition: Permission[] = [
  Permission.INSURANCE_CREATE,
  Permission.INSURANCE_UPDATE,
  Permission.LOAN_CREATE,
  Permission.LOAN_UPDATE,
  Permission.INVESTMENT_CREATE,
  Permission.INVESTMENT_UPDATE,
  Permission.DEPOSIT_CREATE,
  Permission.DEPOSIT_UPDATE,
  Permission.SECOND_PILLAR_CREATE,
  Permission.SECOND_PILLAR_UPDATE,
  Permission.THIRD_PILLAR_CREATE,
  Permission.THIRD_PILLAR_UPDATE,
  Permission.GENERIC_CREATE,
  Permission.GENERIC_UPDATE,
];

const contractExportPredisposition: Permission[] = [
  Permission.INSURANCE_READ,
  Permission.LOAN_READ,
  Permission.INVESTMENT_READ,
  Permission.DEPOSIT_READ,
  Permission.SECOND_PILLAR_READ,
  Permission.THIRD_PILLAR_READ,
  Permission.GENERIC_READ,
];

export const AdminUpdateUserAccountForm = ({ open, user, account, onFormCancel }: Props) => {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();

  const isCurrentUserSystemAdmin = useIsSystemAdmin();
  const topAgentAllowedFeatures = useEnumeration(EnumerationType.TOP_AGENT_ALLOWED_FEATURES);

  const { data: permissionDefinitions } = useGetPermissionDefinitions();

  const [form] = Form.useForm<AdminCreateUpdateAgentUserAccount>();

  const adminUpdateAgentUserAccount = useAdminUpdateAgentUserAccount();

  // Only the admin's changes are kept, the account's own values show until they make one
  const [changedAgentId, setChangedAgentId] = useState<UUID>();
  const [changedPermissions, setChangedPermissions] = useState<Permission[]>();

  const { data: agentsData } = useGetAgentEnumerations(account?.agent.id ? [account?.agent.id] : []);
  const agent = agentsData?.[0];

  const isAccountLoaded = open && !!agent && !!account;
  const selectedAgentId = changedAgentId ?? (isAccountLoaded ? agent.id : undefined);
  const formCheckedPermissions = changedPermissions ?? (isAccountLoaded ? account.permissions : []);

  const contractImportValidation =
    !formCheckedPermissions.includes(Permission.CONTRACT_IMPORT) ||
    contractImportPredisposition.some((predisposition) => formCheckedPermissions.includes(predisposition));
  const contractExportValidation =
    !formCheckedPermissions.includes(Permission.CONTRACT_EXPORT) ||
    contractExportPredisposition.some((predisposition) => formCheckedPermissions.includes(predisposition));
  const contractVerificationExportValidation =
    !formCheckedPermissions.includes(Permission.CONTRACT_VERIFICATION_EXPORT) ||
    contractExportPredisposition.some((predisposition) => formCheckedPermissions.includes(predisposition));

  const { data: selectedAgentsData } = useGetAgentEnumerations(selectedAgentId ? [selectedAgentId] : []);
  const selectedAgent = selectedAgentsData?.[0];

  useEffect(() => {
    if (open && agent && account) {
      form.setFieldsValue({
        optimisticLockVersion: account.optimisticLockVersion,
        disabled: account.disabled,
        agentId: agent.id,
        representingAgentId: agent?.representatives?.find(
          (representative) => representative.id === account.representingAgent?.id,
        )?.id,
      });
    }
  }, [open, account, form, agent]);

  const handleFormCancel = () => {
    onFormCancel();
    setChangedAgentId(undefined);
    setChangedPermissions(undefined);
  };

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (formCheckedPermissions.length === 0) {
          message.error(t("user.helpers.noPermissions"));
        } else if (user && account) {
          adminUpdateAgentUserAccount.mutate(
            { id1: user.id, id2: account.id, object: { ...values, permissions: formCheckedPermissions } },
            {
              onSuccess: () => {
                userChanged(user.id);
                handleFormCancel();
              },
              onError: (error) => setErrorsToForm(form, "user.attrs", error.violations),
            },
          );
        }
      })
      .catch(resolveFormValidationError);
  };

  const handlePermissionTreeCheck = (checked: AntTreeStrictCheckedProps) => {
    const checkedPermissions = checked.checked.map((checked) => Permission[checked as keyof typeof Permission]);
    setChangedPermissions(filterPermissionsWithPrerequisites(permissionDefinitions, checkedPermissions));
  };

  const handleAgentChange = (agentId: UUID) => {
    form.setFieldsValue({ representingAgentId: undefined });
    setChangedAgentId(agentId);
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={
        account
          ? `${t("user.titles.updateAccount")}: ${
              account.representingAgent
                ? `${formatAgentName(account.agent)} | ${formatAgentName(account.representingAgent)}`
                : formatAgentName(account.agent)
            }`
          : t("user.titles.updateAccount")
      }
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={adminUpdateAgentUserAccount.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={handleFormCancel}
      mask={{ closable: false }}
      okButtonProps={{
        disabled: !contractImportValidation || !contractExportValidation || !contractVerificationExportValidation,
      }}
    >
      <Form form={form} layout="vertical" name="userAccountUpdateForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item name="agentId" label={t("user.attrs.agentId")} rules={[validations.notNull]}>
              <AgentSelect onChange={handleAgentChange} />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="representingAgentId"
              label={
                <LabelWithTooltip
                  label={t("user.attrs.representingAgentId")}
                  tooltip={t("user.helpers.representingAgentInfo")}
                />
              }
              rules={[selectedAgent?.type === AgentType.LEGAL ? validations.notNull : validations.none]}
            >
              <AgentSelect
                optionsProps={{
                  allowedIds: selectedAgent?.representatives?.map((representative) => representative.id),
                  forceRender: true,
                }}
                disabled={selectedAgent?.type !== AgentType.LEGAL}
                allowClear
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="disabled"
              className="form-item-without-label"
              valuePropName="checked"
              initialValue={false}
              rules={[validations.none]}
            >
              <Checkbox>{t("user.attrs.disabled")}</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Divider titlePlacement="start" className="divider-subheader">
          {t("user.titles.permissionsSettings")}
        </Divider>

        <DefaultPermissionsFormPart
          allowedFeatures={topAgentAllowedFeatures}
          onPermissionsSet={setChangedPermissions}
        />

        <Row gutter={rowGutter}>
          <Col span={colSpan * 3}>
            <Form.Item label={t("user.attrs.permissionsLabel")} required>
              <Tree
                showLine={{ showLeafIcon: false }}
                checkStrictly
                checkable
                checkedKeys={{ checked: formCheckedPermissions, halfChecked: [] }}
                treeData={buildPermissionTreeNodes(
                  permissionDefinitions,
                  topAgentAllowedFeatures,
                  formCheckedPermissions,
                  isCurrentUserSystemAdmin,
                  user?.agentUserRole?.systemAdmin,
                )}
                onCheck={(data) => handlePermissionTreeCheck(data as AntTreeStrictCheckedProps)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {contractImportValidation ? undefined : (
        <Alert
          className="margin-bottom-small"
          title={`${t("user.helpers.permissionValidationMessage", { permission: t(`user.enums.permission.${Permission.CONTRACT_IMPORT}`) })}: ${contractImportPredisposition.map((predisposition) => t(`user.enums.permission.${predisposition}`)).join(", ")}.`}
          type="warning"
          showIcon
        />
      )}

      {contractExportValidation ? undefined : (
        <Alert
          className="margin-bottom-small"
          title={`${t("user.helpers.permissionValidationMessage", { permission: t(`user.enums.permission.${Permission.CONTRACT_EXPORT}`) })}: ${contractExportPredisposition.map((predisposition) => t(`user.enums.permission.${predisposition}`)).join(", ")}.`}
          type="warning"
          showIcon
        />
      )}

      {contractVerificationExportValidation ? undefined : (
        <Alert
          className="margin-bottom-small"
          title={`${t("user.helpers.permissionValidationMessage", { permission: t(`user.enums.permission.${Permission.CONTRACT_VERIFICATION_EXPORT}`) })}: ${contractExportPredisposition.map((predisposition) => t(`user.enums.permission.${predisposition}`)).join(", ")}.`}
          type="warning"
          showIcon
        />
      )}

      {user && hasConfirmedAgentUserAccount(user) && (
        <Alert title={t("user.helpers.updateInfo")} type="info" showIcon />
      )}
    </Modal>
  );
};
